import { ApiError } from '@dataroom/shared';
import type { BillingArchivePack, PlanLimitCode, TenantPlanTier } from '@dataroom/shared';

export const TIER_LABELS: Record<TenantPlanTier, string> = {
  free_trial: 'Free Trial',
  starter: 'Starter',
  buying_office: 'Plus',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

/** "$99", "$1,908", "$12.50" from integer cents (whole dollars drop the cents). */
export function formatUsd(cents: number): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(cents / 100);
}

/** "50 a day" / "Unlimited" for a plan's daily "Ask the Data Room" allowance (null = unlimited). */
export function askCapLabel(askPerDay: number | null): string {
  return askPerDay === null ? 'Unlimited' : `${askPerDay} a day`;
}

/** "Need more archived rooms? Add 10 for $10/month." — from the server's archive-pack price. */
export function archivePackLine(pack: BillingArchivePack): string {
  return `Need more archived rooms? Add ${pack.rooms} for ${formatUsd(pack.monthlyCents)}/month.`;
}

/** Shown when a billing call answers 503 `billing_not_configured` (Stripe dormant). */
export const BILLING_NOT_CONFIGURED_MESSAGE = "Billing isn't set up yet.";

export function isBillingNotConfigured(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 503 &&
    error.problem?.code === 'billing_not_configured'
  );
}

/** A user-facing message for a failed billing call — friendly on the dormant 503,
 * the API's own plain text otherwise. Null when there is no error. */
export function billingErrorMessage(error: unknown): string | null {
  if (!error) return null;
  if (isBillingNotConfigured(error)) return BILLING_NOT_CONFIGURED_MESSAGE;
  if (error instanceof ApiError) return error.message;
  return 'Request failed.';
}

export interface PlanLimitInfo {
  code: PlanLimitCode;
  limit: number | null;
  current: number | null;
  detail: string | null;
}

const PLAN_LIMIT_CODES: readonly string[] = [
  'active_limit_reached',
  'archived_limit_reached',
  'seat_limit_reached',
];

/** Detect a plan-limit 409 (an ApiError carrying one of the limit `code`s) and
 * return its parsed details; null for any other error. */
export function planLimitOf(error: unknown): PlanLimitInfo | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const code = error.problem?.code;
  if (!code || !PLAN_LIMIT_CODES.includes(code)) return null;
  return {
    code: code as PlanLimitCode,
    limit: error.problem?.limit ?? null,
    current: error.problem?.current ?? null,
    detail: error.problem?.detail ?? null,
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days left in a trial (rounded up); <= 0 means the trial has ended. */
export function trialDaysLeft(trialEndsAt: string, now: number = Date.now()): number {
  return Math.ceil((new Date(trialEndsAt).getTime() - now) / DAY_MS);
}

/** "1 active room" / "3 active rooms" from the plural noun (the trial's plan-limit
 * dialog read "Your plan includes 1 active rooms"). */
export function planCountOf(n: number, pluralNoun: string): string {
  return `${n} ${n === 1 ? pluralNoun.replace(/s$/, '') : pluralNoun}`;
}
